import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, EnvelopeSimple } from "@phosphor-icons/react";
import CidesLogo from "@/components/CidesLogo";
import { DataFlowBg } from "@/components/DataFlowBg";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  fr: {
    eyebrow: "Site en cours de finalisation",
    title: "Notre nouveau site arrive bientôt.",
    subtitle:
      "Nous préparons une nouvelle expérience pour vous présenter nos solutions d'identité sécurisée et de traçabilité télécom.",
    formTitle: "Restez informé — ou écrivez-nous directement",
    email: "Email professionnel",
    message: "Message (optionnel)",
    submit: "Nous écrire",
    sending: "Envoi…",
    successTitle: "Message envoyé",
    successDesc: "Merci — notre bureau vous répondra sous 48 heures ouvrées.",
    errorTitle: "Envoi impossible",
    errorDesc:
      "Une erreur est survenue. Merci de réessayer ou d'écrire directement à management@cidesinvest.io.",
    invalidEmail: "Veuillez saisir un email valide",
    directEmailLabel: "Nous écrire directement",
    hqLabel: "Siège social",
    hq: "23 Woodvale Avenue, Giffnock, Glasgow, UK, G46 6RG",
    company: "CIDES Invest and Trade Limited — Company n° SC564447",
    langHint: "FR",
    langAlt: "EN",
  },
  en: {
    eyebrow: "Site being finalised",
    title: "Our new website is on its way.",
    subtitle:
      "We are preparing a new experience to present our secure identity and telecom-traceability solutions.",
    formTitle: "Stay informed — or write to us directly",
    email: "Business email",
    message: "Message (optional)",
    submit: "Get in touch",
    sending: "Sending…",
    successTitle: "Message sent",
    successDesc: "Thank you — our office will reply within 48 business hours.",
    errorTitle: "Unable to send",
    errorDesc:
      "An error occurred. Please try again or write directly to management@cidesinvest.io.",
    invalidEmail: "Please enter a valid email",
    directEmailLabel: "Write to us directly",
    hqLabel: "Registered office",
    hq: "23 Woodvale Avenue, Giffnock, Glasgow, UK, G46 6RG",
    company: "CIDES Invest and Trade Limited — Company n° SC564447",
    langHint: "EN",
    langAlt: "FR",
  },
};

const MaintenancePage = () => {
  const { lang, setLang } = useLang();
  const c = COPY[lang] || COPY.fr;
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [err, setErr] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErr(c.invalidEmail);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: "Maintenance-page enquiry",
        organization: "(not provided)",
        country: "(not provided)",
        email,
        message:
          message.trim().length >= 5
            ? message.trim()
            : "Visitor left their email on the maintenance page — please follow up.",
      });
      toast.success(c.successTitle, { description: c.successDesc });
      setEmail("");
      setMessage("");
    } catch (e2) {
      toast.error(c.errorTitle, { description: c.errorDesc });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#0f1826] text-white overflow-hidden">
      <DataFlowBg density={8} />

      {/* Language switch */}
      <div className="absolute top-6 right-6 z-10 flex items-center border border-white/10 text-[11px] uppercase tracking-[0.2em] font-medium">
        <button
          onClick={() => setLang("fr")}
          data-testid="maintenance-lang-fr"
          className={`px-2.5 py-1.5 transition-colors focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2AB0EA] ${
            lang === "fr" ? "bg-[#0089C8] text-white" : "text-slate-300 hover:text-white"
          }`}
        >
          FR
        </button>
        <button
          onClick={() => setLang("en")}
          data-testid="maintenance-lang-en"
          className={`px-2.5 py-1.5 transition-colors focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2AB0EA] ${
            lang === "en" ? "bg-[#0089C8] text-white" : "text-slate-300 hover:text-white"
          }`}
        >
          EN
        </button>
      </div>

      <main
        className="relative min-h-screen flex items-center justify-center px-6 py-16"
        data-testid="maintenance-page"
      >
        <div className="w-full max-w-2xl mx-auto text-center">
          <div className="flex justify-center">
            <CidesLogo height={80} />
          </div>

          <div className="section-eyebrow mt-10">{c.eyebrow}</div>
          <h1 className="h-display mt-5 text-3xl sm:text-4xl md:text-5xl text-white">
            {c.title}
          </h1>
          <p className="mt-6 text-slate-200 text-base sm:text-lg leading-relaxed max-w-xl mx-auto">
            {c.subtitle}
          </p>

          {/* Contact form */}
          <form
            onSubmit={submit}
            className="mt-12 sharp-card p-6 sm:p-8 text-left"
            data-testid="maintenance-form"
            noValidate
          >
            <div className="section-eyebrow text-[10px] text-center">{c.formTitle}</div>

            <div className="mt-6 space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.24em] text-slate-400 font-semibold">
                  {c.email} *
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="maintenance-email"
                  autoComplete="email"
                  className="mt-2 w-full bg-[#17223a] border border-white/10 focus:border-[#0089C8] outline-none px-4 py-3 text-sm text-white placeholder:text-slate-500 transition-colors"
                  placeholder="contact@ministere.gov"
                />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.24em] text-slate-400 font-semibold">
                  {c.message}
                </label>
                <textarea
                  rows={3}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  data-testid="maintenance-message"
                  className="mt-2 w-full bg-[#17223a] border border-white/10 focus:border-[#0089C8] outline-none px-4 py-3 text-sm text-white placeholder:text-slate-500 transition-colors resize-none"
                />
              </div>

              {err && (
                <div className="text-[12px] text-[#FF6B35]" data-testid="maintenance-error">
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={sending}
                data-testid="maintenance-submit"
                className="group w-full inline-flex items-center justify-center gap-3 bg-[#FF6B35] hover:bg-[#E55A2B] disabled:opacity-70 text-white px-6 py-3.5 text-xs uppercase tracking-[0.24em] font-semibold transition-colors focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2AB0EA]"
              >
                {sending ? c.sending : c.submit}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </form>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-[12px] text-slate-300">
            <a
              href="mailto:management@cidesinvest.io"
              className="inline-flex items-center gap-2 hover:text-white transition-colors"
              data-testid="maintenance-email-link"
            >
              <EnvelopeSimple size={14} className="text-[#0089C8]" />
              management@cidesinvest.io
            </a>
          </div>

          <div className="mt-10 pt-8 border-t border-white/[0.08] text-[11px] uppercase tracking-[0.2em] text-slate-400 space-y-1">
            <div>{c.company}</div>
            <div className="text-slate-500 normal-case tracking-normal text-[11px] max-w-md mx-auto">
              {c.hq}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MaintenancePage;
